import { DateTime } from 'luxon';

const BRAZIL_TIMEZONE = 'America/Sao_Paulo';
const DATE_TIME_LOCAL_FORMAT = "yyyy-LL-dd'T'HH:mm";
const DATE_KEY_FORMAT = 'yyyy-LL-dd';

function toDateTime(value?: string | Date | null): DateTime {
  if (!value) {
    return DateTime.invalid('missing date');
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: 'utc' });
  }

  // Date-only strings (YYYY-MM-DD) represent a business date in Brazil — parse
  // them directly in the Brazil timezone so that setZone() calls later do not
  // shift the calendar day when the browser runs in UTC or another zone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return DateTime.fromISO(value, { zone: BRAZIL_TIMEZONE });
  }

  return DateTime.fromISO(value, { setZone: true });
}

function toLocalDateTime(value?: string | Date | null): DateTime {
  if (!value) {
    return DateTime.invalid('missing date');
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return DateTime.fromISO(value, { zone: 'local' });
  }

  return DateTime.fromISO(value, { setZone: true }).setZone('local');
}

export function nowDateTimeLocal(now = DateTime.now()): string {
  return now.setZone('local').toFormat(DATE_TIME_LOCAL_FORMAT);
}

export function formatDateLocal(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const parsed = toLocalDateTime(value || null);
  if (!parsed.isValid) {
    return '-';
  }

  return parsed.toJSDate().toLocaleDateString('pt-BR', {
    ...(options || {}),
  });
}

export function nowDateTimeLocalInBrazil(now = DateTime.now()): string {
  return now.setZone(BRAZIL_TIMEZONE).toFormat(DATE_TIME_LOCAL_FORMAT);
}

export function dateTimeLocalBrazilToIso(localValue?: string | null): string {
  if (!localValue) {
    return '';
  }

  const parsed = DateTime.fromFormat(localValue, DATE_TIME_LOCAL_FORMAT, {
    zone: BRAZIL_TIMEZONE,
  });

  if (!parsed.isValid) {
    return '';
  }

  return parsed.toUTC().toISO({ suppressMilliseconds: true }) || '';
}

export function dateKeyBrazil(value?: string | Date | null): string {
  const parsed = toDateTime(value || null);
  if (!parsed.isValid) {
    return '';
  }
  return parsed.setZone(BRAZIL_TIMEZONE).toFormat(DATE_KEY_FORMAT);
}

export function diffCalendarDaysBrazil(
  from?: string | Date | null,
  to?: string | Date | null,
): number {
  const fromDt = toDateTime(from || null).setZone(BRAZIL_TIMEZONE).startOf('day');
  const toDt = (to ? toDateTime(to) : DateTime.now())
    .setZone(BRAZIL_TIMEZONE)
    .startOf('day');

  if (!fromDt.isValid || !toDt.isValid) {
    return 0;
  }

  const diff = toDt.diff(fromDt, 'days').days;
  if (!Number.isFinite(diff)) {
    return 0;
  }
  return Math.trunc(diff);
}

export function getTodayDateKeyBrazil(now = DateTime.now()): string {
  return now.setZone(BRAZIL_TIMEZONE).toFormat(DATE_KEY_FORMAT);
}

/**
 * Indica se a data informada (YYYY-MM-DD ou Date) cai em um dia posterior ao
 * dia de hoje no fuso de negocio (Brasil). Usado para bloquear datas futuras
 * em campos como a "Data de Entrada" do animal. Valor vazio retorna false.
 */
export function isFutureDateKeyBrazil(
  value?: string | Date | null,
  now = DateTime.now(),
): boolean {
  if (!value) {
    return false;
  }
  const key = dateKeyBrazil(value);
  if (!key) {
    return false;
  }
  return key > getTodayDateKeyBrazil(now);
}

export function formatDateBrazil(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const parsed = toDateTime(value || null);
  if (!parsed.isValid) {
    return '-';
  }

  return parsed
    .setZone(BRAZIL_TIMEZONE)
    .toJSDate()
    .toLocaleDateString('pt-BR', {
      timeZone: BRAZIL_TIMEZONE,
      ...(options || {}),
    });
}
